import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ClipboardText, ArrowCounterClockwise } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const ACTION_STYLES = { create: "bg-emerald-100 text-emerald-700", update: "bg-amber-100 text-amber-700", delete: "bg-rose-100 text-rose-700", restore: "bg-sky-100 text-sky-700" };
const ACTION_LABELS = { create: "Tambah", update: "Ubah", delete: "Hapus", restore: "Pulihkan" };
const ENTITY_LABELS = { service: "Layanan", staff: "Petugas" };

export default function AuditLog() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [logs, setLogs] = useState(null);
  const [confirmLog, setConfirmLog] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/audit-logs").then((r) => setLogs(r.data)).catch(() => setLogs([]));
  useEffect(() => { load(); }, []);

  const doRestore = async () => {
    if (!confirmLog) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/audit-logs/${confirmLog.id}/restore`);
      toast.success(`${ENTITY_LABELS[data.entity] || data.entity} "${data.nama || ""}" berhasil dipulihkan`);
      setConfirmLog(null);
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="animate-fade-up space-y-5" data-testid="audit-page">
      <div className="flex items-center gap-2"><ClipboardText size={26} weight="duotone" className="text-[#7C4DE0]" /><h1 className="font-heading text-3xl font-extrabold text-slate-800">Log Aktivitas</h1></div>
      <p className="text-sm text-slate-500">Catatan siapa menambah, mengubah, atau menghapus Master Layanan &amp; Master Petugas.</p>
      {logs === null ? (
        <p className="text-sm text-slate-400">Memuat...</p>
      ) : logs.length === 0 ? (
        <div data-testid="audit-empty" className="rounded-2xl border border-dashed border-rose-200 bg-white p-10 text-center text-slate-400">Belum ada aktivitas tercatat.</div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-rose-100/70 bg-white shadow-sm">
          <table className="w-full text-sm" data-testid="audit-table">
            <thead className="bg-[#F7F0FF] text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Waktu</th>
                <th className="px-4 py-3">Aktor</th>
                <th className="px-4 py-3">Aksi</th>
                <th className="px-4 py-3">Objek</th>
                {isSuper && <th className="px-4 py-3 text-right">Pemulihan</th>}
              </tr>
            </thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id} data-testid={`audit-row-${l.id}`} className="border-t border-rose-50">
                  <td className="whitespace-nowrap px-4 py-3 text-slate-500">{(l.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-4 py-3">
                    <p className="font-semibold text-slate-800">{l.actor_name || l.actor_email}</p>
                    <p className="text-xs text-slate-400">{l.actor_role}</p>
                  </td>
                  <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${ACTION_STYLES[l.action] || "bg-slate-100 text-slate-600"}`}>{ACTION_LABELS[l.action] || l.action}</span></td>
                  <td className="px-4 py-3"><span className="text-xs text-slate-400">{ENTITY_LABELS[l.entity] || l.entity}:</span> <span className="font-semibold text-slate-700">{l.entity_name}</span></td>
                  {isSuper && (
                    <td className="px-4 py-3 text-right">
                      {l.can_restore ? (
                        <button data-testid={`audit-restore-${l.id}`} onClick={() => setConfirmLog(l)}
                          className="inline-flex items-center gap-1.5 rounded-lg bg-sky-50 px-2.5 py-1 text-xs font-bold text-sky-700 hover:bg-sky-100">
                          <ArrowCounterClockwise size={14} weight="bold" /> Pulihkan
                        </button>
                      ) : l.action === "delete" && l.restored_at ? (
                        <span className="text-xs text-slate-400">Sudah dipulihkan</span>
                      ) : (
                        <span className="text-xs text-slate-300">—</span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!confirmLog} onOpenChange={(v) => !v && setConfirmLog(null)}>
        <DialogContent className="rounded-2xl sm:max-w-sm" data-testid="audit-restore-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-heading">Pulihkan data ini?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">
            {confirmLog && (
              <>Data <b>{ENTITY_LABELS[confirmLog.entity] || confirmLog.entity}: {confirmLog.entity_name}</b> akan dikembalikan menggunakan informasi tepat sebelum dihapus (ID dan seluruh isi aslinya). Jika sudah ada data lain dengan ID atau nama yang sama, pemulihan akan dibatalkan tanpa menimpa data.</>
            )}
          </p>
          <DialogFooter>
            <button data-testid="audit-restore-cancel" onClick={() => setConfirmLog(null)} className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-500">Batal</button>
            <button data-testid="audit-restore-confirm" onClick={doRestore} disabled={busy} className="inline-flex items-center gap-1.5 rounded-xl bg-sky-600 px-5 py-2 text-sm font-bold text-white hover:bg-sky-700 disabled:opacity-60">
              <ArrowCounterClockwise size={16} weight="bold" /> {busy ? "Memulihkan..." : "Pulihkan"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
